import Breadcrumbs from "@/components/Breadcrumbs";
import Button from "@/components/Button";
import ButtonGroup from "@/components/ButtonGroup";
import { Label } from "@/components/chadcn/Label";
import { Separator } from "@/components/chadcn/Separator";
import { DateInput } from "@/components/InputFields";
import Navigation from "@/components/Navigation";
import PageHeader from "@/components/PageHeader";
import SkeletonTextLine from "@/components/SkeletonTextLine";
import Spinner from "@/components/Spinner";
import {
  useBloodPressureObservationsQuery,
  useHypertensionClinicalNotesQuery,
  useHypertensionPatientQuery,
  useOrganizationQuery,
  useUpdateBloodPressureClinicalProtocolMutation,
} from "@/data/queries";
import { HypertensionPatient } from "@/model/Patient";
import { AddClinicalNoteDialog } from "@/screens/patient-details/AddClinicalNoteDialog";
import ClinicalNotesList from "@/screens/patient-details/ClinicalNotesList";
import BloodPressuresTable from "@/screens/patient-details/hypertension/BloodPressuresTable";
import BloodPressuresTableLegend from "@/screens/patient-details/hypertension/BloodPressuresTableLegend";
import HypertensionProtocolBox from "@/screens/patient-details/hypertension/HypertensionProtocolBox";
import OffboardPatientDialog from "@/screens/patient-details/OffboardPatientDialog";
import PatientInfoBox from "@/screens/patient-details/PatientInfoBox";
import SummarySinceLastReviewBanner from "@/screens/patient-details/SummarySinceLastReviewBanner";
import cn from "@/util/cn";
import { DISTANT_FUTURE, DISTANT_PAST, localDateRange } from "@/util/date-utils";
import { useSearchParams } from "@/util/hooks/useSearchParams";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { DayOfWeek, Instant, LocalDate, TemporalAdjusters, ZoneId } from "@js-joda/core";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { UpdateHypertensionProtocolDialog } from "../UpdateHypertensionProtocolDialog";

type QueryParams = {
  start: string;
  end: string;
};

const maxDate = LocalDate.now(ZoneId.SYSTEM).plusDays(1);

const HypertensionPatientDetailsPage = () => {
  const { patientId: patientIdFromParams } = useParams();
  const navigate = useNavigate();
  const patientId = patientIdFromParams!;

  const [searchParams, setSearchParams] = useSearchParams<QueryParams>({
    start: LocalDate.now(ZoneId.SYSTEM)
      .minusDays(21)
      .with(TemporalAdjusters.previous(DayOfWeek.SUNDAY))
      .toString(), // TODO: See if there's a way to base this on the last reviewed date
    end: maxDate.toString(),
  });

  const [isClinicalNoteDialogOpen, setIsClinicalNoteDialogOpen] = useState(false);
  const [isOffboardDialogOpen, setIsOffboardDialogOpen] = useState(false);
  const [isEditingProtocol, setIsEditingProtocol] = useState(false);

  const setStartDate = (startDate: LocalDate) =>
    setSearchParams((prev) => ({ ...prev, start: startDate.toString() }));

  const setEndDate = (endDate: LocalDate) =>
    setSearchParams((prev) => ({ ...prev, end: endDate.toString() }));

  const { start: startDateString, end: endDateString } = searchParams;
  const startDate = LocalDate.parse(startDateString);
  const endDate = LocalDate.parse(endDateString);
  const tableStart = startDate.atStartOfDay(ZoneId.SYSTEM).toInstant();
  const tableEndExclusive = endDate.plusDays(1).atStartOfDay(ZoneId.SYSTEM).toInstant();

  const {
    isLoading: isLoadingPatient,
    isError: isErrorPatient,
    data: patient = null,
  } = useHypertensionPatientQuery(patientId);

  const {
    isPending: isLoadingObservations,
    isError: isErrorObservations,
    data: { observations = [] } = {},
  } = useBloodPressureObservationsQuery({
    patientId: patientId,
    from: tableStart,
    to: tableEndExclusive,
  });

  const {
    mutateAsync: updateThresholds,
    isPending: isUpdatingThresholds,
    isError: isErrorUpdatingThresholds,
  } = useUpdateBloodPressureClinicalProtocolMutation({ patientId });

  const {
    isPending: isLoadingClinicalNotes,
    isError: isErrorClinicalNotes,
    data: clinicalNotes = [],
  } = useHypertensionClinicalNotesQuery(patientId);

  const {
    data: organization,
    //TODO: isError: isErrorOrganization
  } = useOrganizationQuery();

  const navigateToDiabetes = () => navigate(`/patients/${patientId}/diabetes`);

  // -1 ms to make sure first reading is included
  const lastReviewTimeForTable = isLoadingPatient
    ? DISTANT_FUTURE
    : (patient?.lastReviewTime ?? patient?.firstReadingTime?.minusMillis(1) ?? DISTANT_PAST);

  const dates = localDateRange({
    startInclusive: startDate,
    endExclusive: endDate.plusDays(1),
  });

  const isInTable = (time: Instant) =>
    time.equals(tableStart) || (time.isAfter(tableStart) && time.isBefore(tableEndExclusive));

  const patientName = patient ? `${patient.firstName} ${patient.lastName}` : "";

  // Only if the current time is inside the time range defined by the table,
  // and the last review time is on or after the table's start, are we sure
  // that all the unreviewed data (new readings and cells without a reading
  // that have not yet been reviewed) is within the current view. If the
  // current time is not in the currently visible table's time range, there
  // almost definitially could be new data as the time has progressed since
  // the last review. If the last review time is before the table start,
  // there is unreviewed data in a previous time period.
  const currentTableContainsAllNewReadings =
    isInTable(Instant.now()) && lastReviewTimeForTable.compareTo(tableStart) >= 0;

  const breadcrumbs = [{ title: "Patient overview", href: "/" }, { title: patientName }];

  const navigateToPatientOverview = () => {
    navigate("/", { replace: true });
  };

  return (
    <>
      <div className="flex h-full flex-col">
        <Navigation className="px-8 py-4 print:hidden" />
        <Breadcrumbs breadcrumbs={breadcrumbs} className="mb-5 mt-4 shrink-0 px-8 print:hidden" />
        <div className="h-full overflow-y-auto px-8 pb-8 pe-4 print:pb-8">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              {isLoadingPatient ? (
                <SkeletonTextLine className="my-0.5 mb-5 h-9 w-64" />
              ) : (
                <PageHeader title={patientName} className="mb-5" />
              )}
              {patient?.hasDiabetes && (
                <ButtonGroup
                  aria-label="Protocol type"
                  values={[
                    { value: "diabetes", title: "Diabetes" },
                    { value: "hypertension", title: "Hypertension" },
                  ]}
                  value={"hypertension"}
                  onChange={navigateToDiabetes}
                  className="print:hidden"
                />
              )}
            </div>
            {!isLoadingPatient && (
              <Menu as="div" className="relative print:hidden">
                <MenuButton as={Button} variant="neutral" className="material-icons p-2">
                  more_vert
                </MenuButton>
                <MenuItems
                  transition
                  anchor="bottom end"
                  className="absolute right-0 z-10 mt-2 w-48 rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 transition duration-200 ease-out focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150"
                >
                  <MenuItem>
                    <button
                      onClick={() => setIsOffboardDialogOpen(true)}
                      className="block w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100"
                    >
                      Offboard patient
                    </button>
                  </MenuItem>
                </MenuItems>
              </Menu>
            )}
          </div>
          <SummarySinceLastReviewBanner
            patientStatus={patient?.status}
            lastReviewTime={patient?.lastReviewTime}
            unreviewedReadingsSummaryData={patient ? summaryDataFor(patient) : []}
            isLoading={isLoadingPatient || !patient?.unreviewedReadingsUpToDate}
            className="mb-4 print:hidden"
          />
          <div className="mb-8 flex gap-4">
            <PatientInfoBox
              isLoading={isLoadingPatient}
              patient={patient}
              className={cn("max-w-1/2 w-fit", isLoadingPatient ? "w-96" : "")}
            />
            <HypertensionProtocolBox
              thresholds={patient?.thresholds}
              protocolName={patient?.personalizedClinicalProtocol.name ?? ""}
              isLoading={isLoadingPatient}
              onEditSelected={() => setIsEditingProtocol(true)}
            />
          </div>
          <div className="flex items-center gap-2 pb-4 print:hidden">
            <Label htmlFor="start-date-input">From</Label>
            <DateInput
              id="start-date-input"
              className="max-w-36 grow"
              max={endDate.toString()}
              value={startDate.toString()}
              onChange={(e) => setStartDate(LocalDate.parse(e.target.value))}
            />
            <Label htmlFor="end-date-input">to</Label>
            <DateInput
              id="end-date-input"
              className="max-w-36 grow"
              min={startDate.toString()}
              max={maxDate.toString()}
              value={endDate.toString()}
              onChange={(e) => setEndDate(LocalDate.parse(e.target.value))}
            />
            {isLoadingObservations && <Spinner className="ms-4" />}
          </div>
          {isErrorPatient && (
            <div className="py-2 text-red-400">
              Failed to retrieve patient details. Please try again later.
            </div>
          )}
          {isErrorObservations && (
            <div className="py-2 text-red-400">Failed to load chart. Please try again later.</div>
          )}
          <h2 className="mb-4 hidden text-2xl text-primary/90 print:block print:break-before-page">
            Measurements
          </h2>
          <BloodPressuresTable
            dates={dates}
            observations={observations}
            lastReviewTime={lastReviewTimeForTable}
            className="w-full"
          />
          <BloodPressuresTableLegend className="mt-3" />
          <Separator orientation="horizontal" className="mt-8 print:hidden" />
          <div className="mb-4 mt-8 flex items-center justify-between print:break-before-page">
            <h2 className="text-2xl text-primary/90">Clinical notes</h2>
            <Button
              variant="primary"
              disabled={isLoadingClinicalNotes}
              className="ml-auto print:hidden"
              onClick={() => setIsClinicalNoteDialogOpen(true)}
            >
              <span className="material-icons me-1">add</span>Add note
            </Button>
          </div>
          <ClinicalNotesList
            clinicalNotes={clinicalNotes}
            filterOptionsForPrinting={{
              timeRangeStart: tableStart,
              timeRangeEnd: tableEndExclusive,
            }}
          />
          {isLoadingClinicalNotes && <Spinner />}
          {isErrorClinicalNotes ? (
            <p className="py-2 text-red-400">
              Failed to load clinical notes. Please try again later.
            </p>
          ) : !isLoadingClinicalNotes && clinicalNotes.length === 0 ? (
            <div className="italic text-primary/60">No notes available</div>
          ) : null}
        </div>
      </div>
      {organization && patient && (
        <AddClinicalNoteDialog
          open={isClinicalNoteDialogOpen}
          onOpenChange={setIsClinicalNoteDialogOpen}
          patientId={patientId}
          protocolType="hypertension"
          prescribingCaregiver={patient.prescribingCaregiver}
          currentTableContainsAllNewReadings={currentTableContainsAllNewReadings}
          organization={organization}
        />
      )}
      {patient && (
        <OffboardPatientDialog
          open={isOffboardDialogOpen}
          onOpenChange={setIsOffboardDialogOpen}
          patient={patient}
          prescriptionId={patient.prescriptionId || ""}
          deliveryDate={patient.deliveryDate}
          onConfirm={navigateToPatientOverview}
        />
      )}
      {patient && organization && (
        <UpdateHypertensionProtocolDialog
          open={isEditingProtocol}
          onOpenChange={setIsEditingProtocol}
          isLoading={isUpdatingThresholds}
          isError={isErrorUpdatingThresholds}
          diagnosis={patient.diagnosis}
          personalizedClinicalProtocol={patient.personalizedClinicalProtocol}
          organizationClinicalProtocols={organization.hypertensionClinicalProtocols}
          onSubmit={async (thresholdData) => {
            try {
              const { clinicalProtocolId, ...thresholds } = thresholdData;
              await updateThresholds({
                prescriptionId: patient.prescriptionId,
                clinicalProtocolId,
                thresholds,
              });
              setIsEditingProtocol(false);
            } catch (error) {
              console.error(error);
            }
          }}
        />
      )}
    </>
  );
};

const summaryDataFor = (patient: HypertensionPatient) => {
  const { critical, total } = patient.unreviewedReadings;
  const nonCritical = total - critical;

  const criticalSummaryData = {
    amount: critical,
    classificationText: "critical high",
    className: "text-measurement-text-emergency",
  };

  const nonCriticalSummaryData = {
    amount: nonCritical,
    classificationText: nonCritical === total ? undefined : "other",
    className: "text-primary/90",
  };

  return [criticalSummaryData, nonCriticalSummaryData];
};

export default HypertensionPatientDetailsPage;
